// Encapsulates every piece of gesture physics the bottom sheet
// needs: a spring for the open/close motion, 1:1 pointer tracking
// while dragging, rubber-banding past the top edge, and a flick
// (velocity-projected) dismiss — all ported from the original
// vanilla build's drag handler.

import { useCallback, useRef, useState, useMemo } from 'react';
import type { PointerEvent as ReactPointerEvent, RefObject } from 'react';
import { useSpring } from './useSpring';
import { useReducedMotion } from './useReducedMotion';
import { rubberband, project } from '@/utils/springMath';

interface DragPoint {
  y: number;
  t: number;
}

export interface UseBottomSheetResult {
  sheetRef: RefObject<HTMLDivElement>;
  isOpen: boolean;
  isMounted: boolean;
  open: () => void;
  close: (velocity?: number) => void;
  dragHandlers: {
    onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
  };
}

const DISMISS_VELOCITY_THRESHOLD = 700;
const DISMISS_DISTANCE_RATIO = 0.32;
const UNMOUNT_DELAY_MS = 460;

export function useBottomSheet(): UseBottomSheetResult {
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const sheetHeightRef = useRef(0);
  const historyRef = useRef<DragPoint[]>([]);
  const draggingRef = useRef(false);
  const dragStartYRef = useRef(0);
  const dragStartValueRef = useRef(0);
  const unmountTimerRef = useRef<number | null>(null);

  const reducedMotion = useReducedMotion();

  const applyTransform = useCallback((value: number) => {
    if (sheetRef.current) {
      sheetRef.current.style.transform = `translateY(${value}px)`;
    }
  }, []);

  const spring = useSpring(0, {
    damping: 1.0,
    response: 0.38,
    onUpdate: applyTransform,
  });

  const open = useCallback(() => {
    if (unmountTimerRef.current !== null) {
      window.clearTimeout(unmountTimerRef.current);
      unmountTimerRef.current = null;
    }
    setIsMounted(true);
    requestAnimationFrame(() => {
      const height = sheetRef.current?.offsetHeight || 420;
      sheetHeightRef.current = height;
      if (reducedMotion) {
        spring.jumpTo(0);
      } else {
        spring.jumpTo(height);
        requestAnimationFrame(() => spring.setTarget(0));
      }
      setIsOpen(true);
    });
  }, [spring, reducedMotion]);

  const close = useCallback(
    (velocity = 0) => {
      setIsOpen(false);
      if (reducedMotion) {
        spring.jumpTo(sheetHeightRef.current);
        setIsMounted(false);
        return;
      }
      spring.setTarget(sheetHeightRef.current, velocity);
      unmountTimerRef.current = window.setTimeout(() => setIsMounted(false), UNMOUNT_DELAY_MS);
    },
    [spring, reducedMotion],
  );

  const pushHistory = (y: number, t: number) => {
    const history = historyRef.current;
    history.push({ y, t });
    if (history.length > 5) history.shift();
  };

  const computeVelocity = (): number => {
    const history = historyRef.current;
    if (history.length < 2) return 0;
    const first = history[0];
    const last = history[history.length - 1];
    const dt = (last.t - first.t) / 1000;
    if (dt <= 0) return 0;
    return (last.y - first.y) / dt;
  };

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      draggingRef.current = true;
      event.currentTarget.setPointerCapture(event.pointerId);
      dragStartYRef.current = event.clientY;
      dragStartValueRef.current = spring.getValue();
      spring.stop();
      historyRef.current = [];
      pushHistory(event.clientY, performance.now());
    },
    [spring],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!draggingRef.current) return;
      const delta = event.clientY - dragStartYRef.current;
      let next = dragStartValueRef.current + delta;
      if (next < 0) next = -rubberband(-next, sheetHeightRef.current);
      applyTransform(next);
      spring.jumpTo(next);
      pushHistory(event.clientY, performance.now());
    },
    [spring, applyTransform],
  );

  const endDrag = useCallback(() => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    const velocity = computeVelocity();
    const projected = spring.getValue() + project(velocity);
    const dismissThreshold = sheetHeightRef.current * DISMISS_DISTANCE_RATIO;
    if (projected > dismissThreshold || velocity > DISMISS_VELOCITY_THRESHOLD) {
      close(velocity);
    } else {
      spring.setVelocity(velocity);
      spring.setTarget(0);
    }
  }, [spring, close]);

  const dragHandlers = useMemo(
    () => ({
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
    }),
    [onPointerDown, onPointerMove, endDrag],
  );

  return useMemo(
    () => ({ sheetRef, isOpen, isMounted, open, close, dragHandlers }),
    [isOpen, isMounted, open, close, dragHandlers],
  );
}
